import type { ExpressShippingPolicy } from '../../../../infrastructure/ShippingClient';
import { CountryCode } from '../../../../models/Currency';
import type { ExpressShippingFieldErrors } from './types';

const EXPRESS_DEFAULTS: Readonly<Partial<Record<CountryCode, ExpressShippingPolicy>>> = {
  [CountryCode.US]: {
    enabled: false,
    shippingFee: 12,
    freeShippingThreshold: 130,
    shippingDuration: { minDay: 4, maxDay: 6 },
  },
  [CountryCode.CA]: {
    enabled: false,
    shippingFee: 16,
    freeShippingThreshold: 190,
    shippingDuration: { minDay: 5, maxDay: 7 },
  },
};

export const supportsExpressShipping = (countryCode: CountryCode): boolean =>
  countryCode === CountryCode.US || countryCode === CountryCode.CA;

export const getDefaultExpressShipping = (countryCode: CountryCode): ExpressShippingPolicy | undefined => {
  const policy = EXPRESS_DEFAULTS[countryCode];
  if (!policy) {
    return undefined;
  }
  return {
    ...policy,
    shippingDuration: { ...policy.shippingDuration },
  };
};

export const validateExpressShipping = (policy: ExpressShippingPolicy): ExpressShippingFieldErrors => {
  const errors: ExpressShippingFieldErrors = {};
  if (!Number.isFinite(policy.shippingFee) || policy.shippingFee < 0) {
    errors.shippingFee = 'Express shipping fee must be zero or greater';
  }
  if (!Number.isFinite(policy.freeShippingThreshold) || policy.freeShippingThreshold < 0) {
    errors.freeShippingThreshold = 'Express free shipping threshold must be zero or greater';
  }
  if (!Number.isFinite(policy.shippingDuration.minDay) || policy.shippingDuration.minDay < 1) {
    errors.minDay = 'Express minimum days must be at least 1';
  }
  if (!Number.isFinite(policy.shippingDuration.maxDay) || policy.shippingDuration.maxDay < 1) {
    errors.maxDay = 'Express maximum days must be at least 1';
  } else if (policy.shippingDuration.minDay > policy.shippingDuration.maxDay) {
    errors.maxDay = 'Express maximum days must be greater than or equal to minimum days';
  }
  return errors;
};
